import { useQuery } from '@tanstack/react-query';
import { deliveryClient } from '../../../lib/connect/clients';
import { useDutiesFanOut } from '../CommandCenter/hooks';
import { OccurrenceWindow, useOccurrencesFanOut } from '../Deliverables/hooks';
import { addDays } from '../Deliverables/lib/format';
// Phase 5c — the delivery calendar reads cross-project deadlines (PoP
// option-period expirations, protest/debrief windows) that the background
// syncer materializes into delivery.calendar_events. ryugu projects
// the rows PER PROGRAM, so a NoView program's events are dropped and disclosed
// as hidden_count. Materialization is a background/admin op (ryugu deliberately
// does not expose a browser sync RPC) — the UI reads and refetches only.
const KEY = 'delivery-calendar';
export function useDeliveryCalendar(filter) {
    return useQuery({
        queryKey: [
            KEY,
            filter
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.getDeliveryCalendar({
                companyId: filter.companyId,
                eventType: filter.eventType ?? ''
            });
            return {
                // Deliverable due dates are the contract model's (below); the old
                // model's deliverable_due rows are dropped so no date shows twice.
                events: resp.events.filter((e)=>e.eventType !== 'deliverable_due'),
                // DRIFT-2: NoView programs' events are dropped server-side; surface how
                // many so deadlines for hidden programs are disclosed, never silent.
                hiddenCount: resp.hiddenCount
            };
        },
        enabled: !!filter.companyId,
        staleTime: 30000
    });
}
// ---------------------------------------------------------------------------
// Deliverable due dates (contract model v2, D113): one ListOccurrences per
// program over the visible month, folded here.
// ---------------------------------------------------------------------------
/** "2026-09" → the month's first and last calendar day. */ export function monthSpan(month) {
    const [y, m] = month.split('-').map(Number);
    const from = `${month}-01`;
    const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
    return {
        from,
        to: addDays(next, -1)
    };
}
/** "2026-09" shifted by n months. */ export function shiftMonth(month, n) {
    const [y, m] = month.split('-').map(Number);
    const t = y * 12 + (m - 1) + n;
    return `${Math.floor(t / 12)}-${String(t % 12 + 1).padStart(2, '0')}`;
}
export function useDeliverableDueDates(programIds, month) {
    const { from, to } = monthSpan(month);
    const results = useOccurrencesFanOut(programIds, {
        window: OccurrenceWindow.ALL,
        dueFrom: from,
        dueTo: to
    });
    const duties = useDutiesFanOut(programIds);
    const dates = [];
    programIds.forEach((programId, i)=>{
        for (const occurrence of results[i]?.data?.occurrences ?? []){
            const duty = duties.get(occurrence.dutyId);
            dates.push({
                programId,
                occurrence,
                title: duty?.title ?? '',
                finalReviewTier: duty?.finalReviewTier ?? ''
            });
        }
    });
    return {
        dates,
        isLoading: results.some((q)=>q.isLoading),
        failed: results.filter((q)=>q.isError).length,
        isFetching: results.some((q)=>q.isFetching),
        refetch: ()=>{
            for (const q of results)void q.refetch();
        }
    };
}
